import React, { useCallback, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { CarbonUser } from 'src/@types/carbonUser';
import {
  IMerchant,
  IMerchantConfig,
  IPatchMerchantApiData,
  ReferrerMerchantConfig,
  ReferrerMerchantType,
} from 'src/@types/merchant';
import { getActivePortalConfigForMerchantApi } from 'src/api/cmsApi';
import { getMerchantByIdApi, patchMerchant } from 'src/api/merchantApi';
import useAuth from 'src/hooks/useAuth';
import useCarbonUser from 'src/hooks/useCarbonUser';

//
//

type AdminUser = { merchantId: string; role: string };

type MerchantContextType = {
  merchant: IMerchant | null;
  isMerchantLoading: boolean;
  config: IMerchantConfig | null;
  getMerchantUsers: () => void;
  merchantUsers: IMerchant[] | null;
  getMerchantConfigByCode: (code?: string) => ReferrerMerchantConfig | undefined;
  updateMerchant: (merchantCode: string, data: IPatchMerchantApiData) => Promise<void>;
  updatePortalConfig: () => Promise<void>;
  getMerchant: (user: CarbonUser | null, merchantId?: string) => Promise<void>;
  getAdminRelationshipFromUser: (user: CarbonUser, merchantId?: string) => AdminUser | null;
};

export const MerchantContext = React.createContext<MerchantContextType>({
  merchant: null,
  merchantUsers: [],
  config: null,
  isMerchantLoading: false,
  getMerchantUsers: () => {},
  getMerchantConfigByCode: () => undefined,
  updateMerchant: async () => {},
  updatePortalConfig: async () => {},
  getMerchant: async () => {},
  getAdminRelationshipFromUser: () => null,
});

type MerchantProviderProps = {
  children: React.ReactNode;
};

//

export const MerchantProvider = ({ children }: MerchantProviderProps) => {
  const [merchant, setMerchant] = useState<IMerchant | null>(null);
  const [merchantIsLoading, setMerchantIsLoading] = useState(true);
  const [merchantUsers, setMerchantUsers] = useState<IMerchant[] | null>(null);
  const [merchantConfig, setMerchantConfig] = useState<IMerchantConfig | null>(null);

  const { carbonUser } = useCarbonUser();
  const { isAuthenticated } = useAuth();

  const updatePortalConfig = async () => {
    if (!merchant?.code) return;

    const response = await getActivePortalConfigForMerchantApi(merchant.code);

    if (!response?.data) return;

    setMerchantConfig((prev) => ({
      configuration: { ...prev?.configuration, ...response?.data?.configuration },
    }));
  };

  const getAdminRelationshipFromUser = (user: CarbonUser, merchantId?: string) => {
    const isStoredMerchantBelongsToUser = user?.merchantUsers?.find(
      (user) => user?.merchantId === merchantId
    );

    if (isStoredMerchantBelongsToUser) {
      return isStoredMerchantBelongsToUser;
    }

    return (
      user?.merchantUsers?.find((item) => item.role === 'ADMIN' || item.role === 'SUPER_USER') ||
      null
    );
  };

  //

  const getMerchantUsers = async () => {
    if (carbonUser?.merchantUsers) {
      const merchantIds = carbonUser?.merchantUsers?.map((user) => user?.merchantId);

      if (!merchantIds?.length) return;

      const records = await Promise.all(
        merchantIds.map(async (record: string) => {
          const response = await getMerchantByIdApi(record, true);

          if (!response?.data?.id) return;

          return response?.data;
        })
      );

      setMerchantUsers(records.filter((v) => !!v?.id));
    }
  };

  const getMerchant = useCallback(async (user: CarbonUser | null, id?: string) => {
    if (!user?.id || !user?.merchantUsers?.length) {
      setMerchantIsLoading(false);
      return;
    }

    const adminUser = getAdminRelationshipFromUser(user, id);

    if (!adminUser?.merchantId) return;

    try {
      const result = await getMerchantByIdApi(adminUser.merchantId, true);

      if (!result?.data?.code) return;

      try {
        const config = await getActivePortalConfigForMerchantApi(result?.data?.code);
        if (config?.data) {
          setMerchantConfig({
            // defaults, remove when they are will be received from the backend
            configuration: {
              minimumCo2KgAmountToPayByInvoice: 50000,
              maximumCo2KgAmountToPayByCard: 250000,
              ...config?.data?.configuration,
            },
          });
        }
      } catch (error) {
        setMerchantConfig({
          // defaults, remove when they are will be received from the backend
          configuration: {
            minimumCo2KgAmountToPayByInvoice: 50000,
            maximumCo2KgAmountToPayByCard: 250000,
          },
        });
      }

      setMerchant(result?.data);
    } finally {
      setMerchantIsLoading(false);
    }
  }, []);

  //

  useEffect(() => {
    if (carbonUser && isAuthenticated) {
      const merchantFromLocalStorage = localStorage.getItem('storedMerchantId');
      let storedMerchantId = null;

      if (merchantFromLocalStorage) {
        try {
          storedMerchantId = JSON.parse(merchantFromLocalStorage);
        } catch (error) {
          console.error(error);
        }
      }

      getMerchant(carbonUser, storedMerchantId);
    } else {
      setMerchant(null);
    }
  }, [carbonUser, carbonUser?.id, getMerchant, isAuthenticated]);

  //

  const getMerchantConfigByCode = (code?: string): ReferrerMerchantConfig | undefined => {
    if (!code) return;

    return referrerMerchantConfig.find((config) => config.merchantCode === code);
  };

  const updateMerchant = async (code: string, data: IPatchMerchantApiData) => {
    if (!merchant?.code) return;

    // FIXME: backend needs pledgeTaken as required param
    const response = await patchMerchant(code, { ...data, pledgeTaken: !!merchant?.pledgeTaken });

    if (!response?.data) return;

    enqueueSnackbar({ variant: 'success', message: 'Merchant settings updated successfully' });

    setMerchant((prev) => (prev === null ? null : { ...prev, ...response?.data }));
  };

  //

  return (
    <MerchantContext.Provider
      value={{
        merchant,
        merchantUsers,
        isMerchantLoading: merchantIsLoading,
        config: merchantConfig,
        getMerchant,
        getMerchantUsers,
        getAdminRelationshipFromUser,
        getMerchantConfigByCode,
        updatePortalConfig,
        updateMerchant,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
};

//

const referrerMerchantConfig = [
  {
    merchantCode: 'BUS_NZ_EVENT_DEMO',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: 'Sign up to our Corporate magic programme' },
  },
  {
    merchantCode: 'BUS_NZ_KIIO_DEMO_ACCOUNT',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: 'Sign up to our Corporate magic programme' },
  },
  {
    merchantCode: 'BUS_AE_EXPO_CITY_DUBAI',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: 'Sign up to the Expo City Corporate Travel programme' },
  },
  {
    merchantCode: 'BUS_SG_CHANGI_AIRPORT',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: '' },
  },
  {
    merchantCode: 'BUS_NZ_CARBONCLICK',
    referrerMerchantRelationshipType: ReferrerMerchantType.PARTNER,
    signUp: { title: '' },
  },
  {
    merchantCode: 'BUS_GB_HELEN_WEBB_EVENTS',
    referrerMerchantRelationshipType: ReferrerMerchantType.EVENT,
    signUp: { title: '' },
  },
];
